import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CONFIRMACOES,
  DESTINACOES_VENDA,
  formatarCnpj,
  REGIMES_CONTRAPARTE,
  ROTULO_DESTINACAO_VENDA,
  ROTULO_FONTE_REGIME,
  ROTULO_REGIME_CONTRAPARTE,
  type Confirmacao,
  type DestinacaoVenda,
  type FonteRegime,
  type RegimeContraparte,
  type SugestaoRegime,
} from '@via-tax/contracts'
import { Aviso, Botao, Campo, Entrada, EstadoVazio, Selecao, Selo } from '@via-tax/ui'
import { CasoConcreto } from '../components/CasoConcreto'
import { TriagemContrapartes } from '../components/TriagemContrapartes'
import { useState } from 'react'
import { dataBr, mesCurto, reais } from '../formatos'
import type { ContextoSessao } from '../tipos'
import { mensagemDeErro, useTRPC } from '../trpc'

/**
 * Contrapartes (arquitetura §8). Três telas sobre o mesmo dado: fornecedores
 * (regime → crédito), clientes (mix de destinação) e crédito em risco.
 * Nenhum valor de imposto aqui — valores de operação; o crédito em R$ é da F5.
 */

const TOM_CREDITO = { INTEGRAL: 'ok', LIMITADO: 'alerta', NENHUM: 'alerta', INDEFINIDO: 'neutro' } as const
const ROTULO_CREDITO = { INTEGRAL: 'crédito integral', LIMITADO: 'crédito limitado', NENHUM: 'sem crédito', INDEFINIDO: 'crédito indefinido' } as const
const docDe = (tipo: string, doc: string | null) => (tipo === 'CPF' ? 'Pessoa física (CPF protegido)' : doc ? formatarCnpj(doc) : '—')
const pct = (x: number) => `${(x * 100).toFixed(1).replace('.', ',')}%`

function Regime({ regime, confirmacao, fonte }: { regime: RegimeContraparte; confirmacao: Confirmacao; fonte: FonteRegime | null }) {
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <span>{ROTULO_REGIME_CONTRAPARTE[regime]}</span>
      <Selo tom={confirmacao === 'CONFIRMADO' ? 'ok' : 'neutro'}>{confirmacao === 'CONFIRMADO' ? 'confirmado' : 'presumido'}</Selo>
      {fonte && <span className="text-[11px] text-tinta-3">{ROTULO_FONTE_REGIME[fonte]}</span>}
    </span>
  )
}

// ─── Fornecedores ────────────────────────────────────────────────────────────

export function Fornecedores({ contexto, irPendencias }: { contexto: ContextoSessao; irPendencias: () => void }) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const pode = (contexto.capacidades as string[]).includes('definir_regime_contraparte')
  const admin = (contexto.capacidadesTenant as string[]).includes('editar_parametros_fiscais')
  const padrao = useQuery(trpc.contraparte.padraoSimples.queryOptions())
  const atualizar = () => qc.invalidateQueries({ queryKey: trpc.contraparte.pathKey() })
  const perguntar = useMutation(trpc.pendencia.abrirRegimeFornecedores.mutationOptions({ onSuccess: () => qc.invalidateQueries({ queryKey: trpc.pendencia.pathKey() }) }))
  const mudarPadrao = useMutation(trpc.contraparte.definirPadraoSimples.mutationOptions({ onSuccess: atualizar }))

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Fornecedores (regime)</h1>
          <p className="mt-1 max-w-2xl text-sm text-tinta-2">
            O regime do fornecedor decide quanto da compra vira crédito de IBS/CBS. Simples puro × híbrido não está em nenhuma base pública: só se sabe perguntando.
          </p>
        </div>
        {pode && (
          <div className="flex flex-wrap gap-2">
            <Botao onClick={() => perguntar.mutate({ limite: 20 })} disabled={perguntar.isPending} title="Abre pendência para os 20 fornecedores do Simples ou desconhecidos de maior compra">
              Perguntar ao cliente (20 maiores)
            </Botao>
          </div>
        )}
      </header>

      <TriagemContrapartes papel="FORNECEDOR" />

      {padrao.data && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-linha bg-painel px-4 py-3 text-sm">
          <span className="text-tinta-2">Fornecedor do Simples sem confirmação é tratado como</span>
          {admin ? (
            <Selecao className="w-auto" value={padrao.data.regimeSnPadrao} onChange={(e) => mudarPadrao.mutate({ regimeSnPadrao: e.target.value as 'SN_PURO' | 'SN_HIBRIDO' })}>
              <option value="SN_PURO">Simples puro (conservador)</option>
              <option value="SN_HIBRIDO">Simples híbrido</option>
            </Selecao>
          ) : (
            <strong>{ROTULO_REGIME_CONTRAPARTE[padrao.data.regimeSnPadrao]}</strong>
          )}
          <span className="text-[12px] text-tinta-3">— premissa do escritório; vale para todas as empresas e aparece marcada como presumida.</span>
        </div>
      )}

      {perguntar.isSuccess && (
        <Aviso tom="ok">
          {perguntar.data.abertas} pendência(s) aberta(s){perguntar.data.jaAbertas ? ` · ${perguntar.data.jaAbertas} já estavam na caixa` : ''}.{' '}
          <button className="underline" onClick={irPendencias}>Ver pendências</button>
        </Aviso>
      )}
      {(perguntar.isError || mudarPadrao.isError) && <Aviso>{mensagemDeErro(perguntar.error ?? mudarPadrao.error)}</Aviso>}
      <TabelaContrapartes contexto={contexto} papel="FORNECEDOR" />
    </div>
  )
}

const ROTULO_CREDITO_CLIENTE = { INTEGRAL: 'aproveita o crédito', LIMITADO: 'não aproveita', NENHUM: 'não aproveita', INDEFINIDO: 'a confirmar' } as const
const TOM_CREDITO_CLIENTE = { INTEGRAL: 'ok', LIMITADO: 'neutro', NENHUM: 'neutro', INDEFINIDO: 'neutro' } as const

/**
 * Lista de fornecedores ou clientes com filtro e paginação. Escritório define o regime;
 * a empresa (cliente) informa, e a informação passa pela conferência do escritório.
 */
function TabelaContrapartes({ contexto, papel }: { contexto: ContextoSessao; papel: 'FORNECEDOR' | 'CLIENTE' }) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const [filtro, setFiltro] = useState<{ regime?: RegimeContraparte; confirmacao?: Confirmacao; busca?: string }>({})
  const [busca, setBusca] = useState('')
  const [pagina, setPagina] = useState(1)
  const [editando, setEditando] = useState<string | null>(null)
  const [informando, setInformando] = useState<{ id: string; nome: string; sugestao: SugestaoRegime } | null>(null)
  const [caso, setCaso] = useState<string | null>(null)
  const [informado, setInformado] = useState<string | null>(null)
  const capacidades = contexto.capacidades as string[]
  const pode = capacidades.includes('definir_regime_contraparte')
  const podeInformar = capacidades.includes('informar_regime_contraparte')
  const fornecedor = papel === 'FORNECEDOR'

  const lista = useQuery({ ...trpc.contraparte.listar.queryOptions({ papel, ...filtro, pagina, porPagina: 50 }), placeholderData: keepPreviousData })
  const atualizar = () => qc.invalidateQueries({ queryKey: trpc.contraparte.pathKey() })
  const consultar = useMutation(trpc.pendencia.abrirRegimeContrapartes.mutationOptions({ onSuccess: () => qc.invalidateQueries({ queryKey: trpc.pendencia.pathKey() }) }))
  const d = lista.data
  const mudar = (p: typeof filtro) => {
    setFiltro((f) => ({ ...f, ...p }))
    setPagina(1)
  }

  return (
    <div className="flex flex-col gap-3">
      {informado && (
        <Aviso tom="ok">
          Regime de {informado} enviado ao escritório. Ele passa a valer na simulação depois da conferência — acompanhe em Pendências.
        </Aviso>
      )}
      {pode && fornecedor && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-linha bg-painel px-4 py-3">
          <p className="max-w-2xl text-[13px] text-tinta-2">
            A nota do fornecedor diz se ele é do Simples ou do regime normal, mas <strong>não diz</strong> se o do Simples vai destacar o IBS/CBS (híbrido) ou deixar tudo no DAS
            (puro) — e é isso que define quanto da compra vira crédito. A consulta abre a pergunta já com o que as notas indicam.
          </p>
          <Botao onClick={() => consultar.mutate({ papel: 'FORNECEDOR', limite: 20 })} disabled={consultar.isPending}>
            Consultar os 20 maiores fornecedores
          </Botao>
        </div>
      )}
      {consultar.isSuccess && (
        <Aviso tom={consultar.data.candidatas ? 'ok' : 'info'}>
          {consultar.data.candidatas
            ? `${consultar.data.abertas} pergunta(s) aberta(s)${consultar.data.jaAbertas ? ` · ${consultar.data.jaAbertas} já estavam na caixa` : ''}. O cliente responde em Pendências e o escritório homologa.`
            : 'Nenhum fornecedor com CNPJ a confirmar: todos já têm regime confirmado.'}
        </Aviso>
      )}
      {consultar.isError && <Aviso>{mensagemDeErro(consultar.error)}</Aviso>}
      <form
        className="grid grid-cols-2 gap-2 rounded-lg border border-linha bg-painel p-3 lg:grid-cols-4"
        onSubmit={(e) => {
          e.preventDefault()
          mudar({ busca: busca.trim() || undefined })
        }}
      >
        <Entrada className="col-span-2" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Nome ou CNPJ" />
        <Selecao value={filtro.regime ?? ''} onChange={(e) => mudar({ regime: (e.target.value || undefined) as RegimeContraparte | undefined })}>
          <option value="">Todos os regimes</option>
          {REGIMES_CONTRAPARTE.map((r) => (
            <option key={r} value={r}>
              {ROTULO_REGIME_CONTRAPARTE[r]}
            </option>
          ))}
        </Selecao>
        <Selecao value={filtro.confirmacao ?? ''} onChange={(e) => mudar({ confirmacao: (e.target.value || undefined) as Confirmacao | undefined })}>
          <option value="">Presumidos e confirmados</option>
          {CONFIRMACOES.map((c) => (
            <option key={c} value={c}>
              {c === 'CONFIRMADO' ? 'Só confirmados' : 'Só presumidos'}
            </option>
          ))}
        </Selecao>
      </form>

      {lista.isError && <Aviso>{mensagemDeErro(lista.error)}</Aviso>}
      {d && d.total === 0 ? (
        <EstadoVazio titulo={fornecedor ? 'Nenhum fornecedor' : 'Nenhum cliente'}>
          {fornecedor ? 'Os fornecedores aparecem depois que as notas de entrada chegam.' : 'Os clientes aparecem depois que as notas de saída chegam.'}
        </EstadoVazio>
      ) : (
        <div className="overflow-hidden rounded-lg border border-linha bg-painel">
          <ul className={`divide-y divide-linha ${lista.isFetching ? 'opacity-60' : ''}`}>
            {d?.itens.map((c) => (
              <li key={c.id} className="grid grid-cols-1 gap-2 px-4 py-3 text-[13px] md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1.6fr)_auto] md:items-center md:gap-4">
                <div className="min-w-0">
                  <span className="block truncate font-medium">{c.nome ?? '—'}</span>
                  <span className="num block text-[11px] text-tinta-3">
                    {docDe(c.tipoPessoa, c.documento)}
                    {c.uf ? ` · ${c.uf}` : ''}
                  </span>
                </div>
                <div className="flex items-baseline justify-between gap-2 md:block md:text-right">
                  <span className="text-[11px] text-tinta-3 md:hidden">{fornecedor ? 'Compras' : 'Vendas'}</span>
                  <span className="num whitespace-nowrap">
                    {reais(c.valor)}
                    <span className="block text-[11px] text-tinta-3">{c.documentos} nota(s)</span>
                  </span>
                </div>
                <div className="flex min-w-0 flex-col gap-1">
                  <Regime regime={c.regime} confirmacao={c.confirmacao} fonte={c.fonte} />
                  <span className="flex flex-wrap items-center gap-1.5">
                    {fornecedor ? <Selo tom={TOM_CREDITO[c.credito]}>{ROTULO_CREDITO[c.credito]}</Selo> : <Selo tom={TOM_CREDITO_CLIENTE[c.credito]}>{ROTULO_CREDITO_CLIENTE[c.credito]}</Selo>}
                    {c.regimeDeclarado && <span className="text-[11px] text-tinta-3">nas notas: {c.regimeDeclarado.toLowerCase().replace('_', ' ')}</span>}
                    {c.confirmacao !== 'CONFIRMADO' && c.sugestao.regime && (
                      <Selo tom="navy">
                        sugestão: {c.sugestao.indica === 'SIMPLES' ? 'Simples — confirmar puro ou híbrido' : ROTULO_REGIME_CONTRAPARTE[c.sugestao.regime]}
                      </Selo>
                    )}
                    {c.empresaCarteiraId && <span className="text-[11px] text-tinta-3">empresa da carteira</span>}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1 md:justify-end">
                  <Botao variante="fantasma" onClick={() => setCaso(c.id)} title="As notas desta relação, mês a mês, sem anualizar">
                    Caso concreto
                  </Botao>
                  {pode && (
                    <Botao variante="fantasma" onClick={() => setEditando(c.id)}>
                      Definir
                    </Botao>
                  )}
                  {!pode && podeInformar && c.tipoPessoa === 'CNPJ' && c.confirmacao !== 'CONFIRMADO' && (
                    <Botao variante="fantasma" onClick={() => setInformando({ id: c.id, nome: c.nome ?? docDe(c.tipoPessoa, c.documento), sugestao: c.sugestao })}>
                      Informar regime
                    </Botao>
                  )}
                </div>
              </li>
            ))}
          </ul>
          {d && d.total > 50 && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-linha px-4 py-2.5 text-xs text-tinta-2">
              <span className="num">Página {pagina} de {Math.ceil(d.total / 50)}</span>
              <div className="flex gap-2">
                <Botao variante="fantasma" disabled={pagina <= 1} onClick={() => setPagina((x) => x - 1)}>Anterior</Botao>
                <Botao variante="fantasma" disabled={pagina * 50 >= d.total} onClick={() => setPagina((x) => x + 1)}>Próxima</Botao>
              </div>
            </div>
          )}
        </div>
      )}
      {caso && <CasoConcreto contraparteId={caso} papel={papel} fechar={() => setCaso(null)} />}
      {editando && <DefinirRegime contraparteId={editando} fechar={() => setEditando(null)} aoSalvar={atualizar} />}
      {informando && (
        <InformarRegime
          alvo={informando}
          papel={papel}
          fechar={() => setInformando(null)}
          aoSalvar={() => {
            setInformado(informando.nome)
            qc.invalidateQueries({ queryKey: trpc.pendencia.pathKey() })
          }}
        />
      )}
    </div>
  )
}

const OPCOES_INFORMAR: Record<'FORNECEDOR' | 'CLIENTE', Array<{ v: 'REGULAR' | 'SN_PURO' | 'SN_HIBRIDO' | 'MEI'; l: string }>> = {
  FORNECEDOR: [
    { v: 'SN_HIBRIDO', l: 'Simples, com IBS/CBS por fora (híbrido)' },
    { v: 'SN_PURO', l: 'Simples Nacional, só no DAS (puro)' },
    { v: 'REGULAR', l: 'Lucro Presumido ou Real (regime regular)' },
    { v: 'MEI', l: 'MEI' },
  ],
  CLIENTE: [
    { v: 'REGULAR', l: 'Lucro Presumido ou Real (regime regular)' },
    { v: 'SN_PURO', l: 'Simples Nacional' },
    { v: 'SN_HIBRIDO', l: 'Simples, com IBS/CBS por fora (híbrido)' },
    { v: 'MEI', l: 'MEI' },
  ],
}

function InformarRegime({
  alvo,
  papel,
  fechar,
  aoSalvar,
}: {
  alvo: { id: string; nome: string; sugestao: SugestaoRegime }
  papel: 'FORNECEDOR' | 'CLIENTE'
  fechar: () => void
  aoSalvar: () => void
}) {
  const trpc = useTRPC()
  // A nota dele já diz se é do Simples; nesse caso a escolha é só puro × híbrido.
  const opcoes =
    alvo.sugestao.indica === 'SIMPLES'
      ? ([
          { v: 'SN_HIBRIDO', l: 'Vai destacar o IBS/CBS na nota (híbrido)' },
          { v: 'SN_PURO', l: 'Vai deixar tudo dentro do DAS (puro)' },
          { v: 'REGULAR', l: 'Na verdade não é do Simples (Real/Presumido)' },
        ] as const)
      : OPCOES_INFORMAR[papel]
  const sugerido = alvo.sugestao.regime && opcoes.some((o) => o.v === alvo.sugestao.regime) ? (alvo.sugestao.regime as (typeof opcoes)[number]['v']) : opcoes[0]!.v
  const [regime, setRegime] = useState<'REGULAR' | 'SN_PURO' | 'SN_HIBRIDO' | 'MEI'>(sugerido)
  const [observacao, setObservacao] = useState('')
  const enviar = useMutation(
    trpc.pendencia.informarRegime.mutationOptions({
      onSuccess: () => {
        aoSalvar()
        fechar()
      },
    }),
  )
  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/30 sm:items-center" onClick={fechar}>
      <form
        className="flex w-full max-w-md flex-col gap-4 rounded-t-lg bg-painel p-5 shadow-xl sm:rounded-lg"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault()
          enviar.mutate({ contraparteId: alvo.id, resposta: { regime, observacao: observacao.trim() || undefined } })
        }}
      >
        <div>
          <p className="text-[11px] text-tinta-3">{papel === 'CLIENTE' ? 'Cliente' : 'Fornecedor'}</p>
          <h2 className="text-base font-semibold">{alvo.nome}</h2>
          <p className="mt-1 text-[12px] text-tinta-2">
            {papel === 'CLIENTE'
              ? 'Cliente do regime regular aproveita o crédito de IBS/CBS do que compra da sua empresa.'
              : 'O regime do fornecedor define quanto da compra vira crédito para a sua empresa.'}{' '}
            O escritório confere antes de valer na simulação.
          </p>
          {alvo.sugestao.regime && (
            <p className="mt-2 rounded-md bg-fundo px-3 py-2 text-[12px] text-tinta-2">
              {alvo.sugestao.indica === 'SIMPLES' ? (
                <>
                  <strong>Ele é do Simples Nacional</strong> — {alvo.sugestao.motivo}. Falta o que só ele sabe: a partir de 2027, vai destacar o IBS/CBS na nota (híbrido) ou deixar
                  tudo dentro do DAS (puro)?
                </>
              ) : (
                <>
                  <strong>Sugestão:</strong> {alvo.sugestao.motivo}.
                </>
              )}
            </p>
          )}
        </div>
        <Campo rotulo="Regime">
          <Selecao value={regime} onChange={(e) => setRegime(e.target.value as typeof regime)}>
            {opcoes.map((o) => (
              <option key={o.v} value={o.v}>
                {o.l}
              </option>
            ))}
          </Selecao>
        </Campo>
        <Campo rotulo="Como soube (opcional)">
          <Entrada value={observacao} onChange={(e) => setObservacao(e.target.value)} maxLength={500} placeholder="Ex.: confirmado com o financeiro deles" />
        </Campo>
        {enviar.isError && <Aviso>{mensagemDeErro(enviar.error)}</Aviso>}
        <div className="flex gap-2">
          <Botao variante="primario" type="submit" disabled={enviar.isPending}>
            Enviar ao escritório
          </Botao>
          <Botao variante="fantasma" type="button" onClick={fechar}>
            Cancelar
          </Botao>
        </div>
      </form>
    </div>
  )
}

function DefinirRegime({ contraparteId, fechar, aoSalvar }: { contraparteId: string; fechar: () => void; aoSalvar: () => void }) {
  const trpc = useTRPC()
  const q = useQuery(trpc.contraparte.detalhe.queryOptions({ contraparteId }))
  const [regime, setRegime] = useState<RegimeContraparte | null>(null)
  const [confirmacao, setConfirmacao] = useState<Confirmacao>('CONFIRMADO')
  const [motivo, setMotivo] = useState('')
  const salvar = useMutation(trpc.contraparte.definirRegime.mutationOptions({ onSuccess: () => { aoSalvar(); fechar() } }))
  const c = q.data?.contraparte
  const atual = regime ?? c?.regime ?? 'DESCONHECIDO'

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/30" onClick={fechar}>
      <aside className="flex h-full w-full max-w-xl flex-col overflow-y-auto bg-painel shadow-xl" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-start justify-between border-b border-linha px-5 py-4">
          <div>
            <p className="num text-[11px] text-tinta-3">{c ? docDe(c.tipoPessoa, c.documento) : ''}</p>
            <h2 className="text-base font-semibold">{c?.nome ?? 'Carregando…'}</h2>
          </div>
          <Botao variante="fantasma" onClick={fechar}>✕</Botao>
        </header>
        {c && (
          <form
            className="flex flex-col gap-4 px-5 py-4 text-sm"
            onSubmit={(e) => {
              e.preventDefault()
              salvar.mutate({ contraparteId, regime: atual, confirmacao, motivo })
            }}
          >
            <p className="text-tinta-2">
              Hoje: <Regime regime={c.regime} confirmacao={c.confirmacao} fonte={c.fonte} />
            </p>
            {c.empresaCarteiraId && (
              <p className="text-[12px] text-tinta-3">Empresa da carteira do escritório: o regime acompanha o cadastro no Processos, a menos que seja definido aqui.</p>
            )}
            <Campo rotulo="Regime">
              <Selecao value={atual} onChange={(e) => setRegime(e.target.value as RegimeContraparte)}>
                {REGIMES_CONTRAPARTE.map((r) => (
                  <option key={r} value={r}>{ROTULO_REGIME_CONTRAPARTE[r]}</option>
                ))}
              </Selecao>
            </Campo>
            <Campo rotulo="Certeza">
              <Selecao value={confirmacao} onChange={(e) => setConfirmacao(e.target.value as Confirmacao)}>
                <option value="CONFIRMADO">Confirmado (informação direta do fornecedor ou do cliente)</option>
                <option value="PRESUMIDO">Presumido (entra no número marcado como premissa)</option>
              </Selecao>
            </Campo>
            <Campo rotulo="De onde veio a informação" dica="Fica no histórico do fornecedor e na trilha de auditoria.">
              <Entrada value={motivo} onChange={(e) => setMotivo(e.target.value)} required minLength={3} placeholder="Ex.: e-mail do contador do fornecedor em 10/09" />
            </Campo>
            {salvar.isError && <Aviso>{mensagemDeErro(salvar.error)}</Aviso>}
            <div className="flex gap-2">
              <Botao variante="primario" type="submit" disabled={salvar.isPending}>Salvar</Botao>
              <Botao variante="fantasma" type="button" onClick={fechar}>Cancelar</Botao>
            </div>
            {q.data!.historico.length > 0 && (
              <section>
                <h3 className="mb-2 text-[13px] font-semibold">Histórico</h3>
                <ol className="flex flex-col gap-1.5 text-[12px] text-tinta-2">
                  {q.data!.historico.map((h, i) => (
                    <li key={i} className="rounded border border-linha px-2.5 py-1.5">
                      <span className="num">{dataBr(h.criadoEm)}</span> · {`${h.de ?? '—'} → ${h.para}`}
                      <span className="block text-tinta-3">{h.motivo}{h.usuario ? ` — ${h.usuario}` : ' — automático'}</span>
                    </li>
                  ))}
                </ol>
              </section>
            )}
          </form>
        )}
      </aside>
    </div>
  )
}

// ─── Clientes: mix de destinação ─────────────────────────────────────────────

const COR_DESTINACAO: Record<DestinacaoVenda, string> = {
  B2C: 'bg-navy',
  B2B_COM_CREDITO: 'bg-ok',
  B2B_SEM_CREDITO: 'bg-alerta',
  B2B_CONSUMO_FINAL: 'bg-tinta-3',
  B2B_INDETERMINADO: 'bg-linha',
}

export function MixDestinacao({ contexto, irPendencias }: { contexto: ContextoSessao; irPendencias: () => void }) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const q = useQuery(trpc.contraparte.mixDestinacao.queryOptions({}))
  const pode = (contexto.capacidades as string[]).includes('definir_regime_contraparte')
  const perguntar = useMutation(trpc.pendencia.abrirRegimeContrapartes.mutationOptions({ onSuccess: () => qc.invalidateQueries({ queryKey: trpc.pendencia.pathKey() }) }))
  if (q.isPending) return <p className="text-sm text-tinta-3">Carregando…</p>
  if (q.isError) return <Aviso>{mensagemDeErro(q.error)}</Aviso>
  const { total, competencias } = q.data
  if (!total.total) return <EstadoVazio titulo="Sem vendas">O mix de destinação aparece depois do envio das notas de saída.</EstadoVazio>
  const b2bCredito = total.percentuais.B2B_COM_CREDITO
  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Clientes (mix de destinação)</h1>
          <p className="mt-1 max-w-2xl text-sm text-tinta-2">
            Para quem a empresa vende: consumidor final, empresas que aproveitam o crédito ou empresas que não aproveitam. Cliente com CNPJ do regime regular conta como
            empresa que se credita, mesmo com nota de consumidor final. É o dado central da decisão Simples puro × híbrido.
          </p>
        </div>
        {pode && (
          <Botao onClick={() => perguntar.mutate({ papel: 'CLIENTE', limite: 20 })} disabled={perguntar.isPending} title="Abre pendência para os 20 clientes de maior venda cujo regime ainda não se sabe">
            Perguntar o regime dos clientes (20 maiores)
          </Botao>
        )}
      </header>

      <TriagemContrapartes papel="CLIENTE" />
      {perguntar.isSuccess && (
        <Aviso tom={perguntar.data.candidatas ? 'ok' : 'info'}>
          {perguntar.data.candidatas
            ? `${perguntar.data.abertas} pergunta(s) aberta(s)${perguntar.data.jaAbertas ? ` · ${perguntar.data.jaAbertas} já estavam na caixa` : ''}. `
            : 'Nenhum cliente com CNPJ de regime desconhecido. '}
          <button className="underline" onClick={irPendencias}>
            Ver pendências
          </button>
        </Aviso>
      )}
      {perguntar.isError && <Aviso>{mensagemDeErro(perguntar.error)}</Aviso>}
      <section className="rounded-lg border border-linha bg-painel p-5">
        <p className="text-sm">
          <strong className="num">{pct(b2bCredito)}</strong> das vendas vão para empresas que se creditam; <strong className="num">{pct(total.percentuais.B2C)}</strong> para consumidor final.
        </p>
        <div className="mt-3 flex h-4 overflow-hidden rounded">
          {DESTINACOES_VENDA.map((k) => (total.valores[k] ? <div key={k} className={COR_DESTINACAO[k]} style={{ width: `${total.percentuais[k] * 100}%` }} title={`${ROTULO_DESTINACAO_VENDA[k]}: ${pct(total.percentuais[k])}`} /> : null))}
        </div>
        <ul className="mt-3 grid gap-1.5 text-[13px] sm:grid-cols-2">
          {DESTINACOES_VENDA.map((k) => (
            <li key={k} className="flex items-center gap-2">
              <span className={`inline-block size-2.5 rounded-sm ${COR_DESTINACAO[k]}`} />
              <span className="flex-1">{ROTULO_DESTINACAO_VENDA[k]}</span>
              <span className="num">{reais(total.valores[k])}</span>
              <span className="num w-14 text-right text-tinta-2">{pct(total.percentuais[k])}</span>
            </li>
          ))}
        </ul>
        {total.dependePremissa > 0 && (
          <p className="mt-3 text-[12px] text-tinta-3">
            <span className="num">{reais(total.dependePremissa)}</span> ({pct(total.dependePremissa / total.total)}) depende de regime de cliente ainda não confirmado — o enquadramento dessas vendas pode mudar.
          </p>
        )}
      </section>
      <div className="overflow-x-auto rounded-lg border border-linha bg-painel">
        <table className="w-full min-w-[760px] text-left text-[13px]">
          <thead className="border-b border-linha text-[11px] uppercase tracking-[0.06em] text-tinta-3">
            <tr>
              <th className="px-3 py-2.5 font-medium">Mês</th>
              <th className="px-3 py-2.5 font-medium">Composição</th>
              <th className="px-3 py-2.5 text-right font-medium">B2C</th>
              <th className="px-3 py-2.5 text-right font-medium">B2B c/ crédito</th>
              <th className="px-3 py-2.5 text-right font-medium">B2B s/ crédito</th>
              <th className="px-3 py-2.5 text-right font-medium">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-linha">
            {competencias.map((c) => (
              <tr key={c.competencia}>
                <td className="num px-3 py-2">{mesCurto(c.competencia)}</td>
                <td className="px-3 py-2">
                  <div className="flex h-2.5 w-48 overflow-hidden rounded">
                    {DESTINACOES_VENDA.map((k) => (c.valores[k] ? <div key={k} className={COR_DESTINACAO[k]} style={{ width: `${(c.valores[k] / c.total) * 100}%` }} /> : null))}
                  </div>
                </td>
                <td className="num px-3 py-2 text-right">{pct(c.valores.B2C / c.total)}</td>
                <td className="num px-3 py-2 text-right">{pct(c.valores.B2B_COM_CREDITO / c.total)}</td>
                <td className="num px-3 py-2 text-right">{pct(c.valores.B2B_SEM_CREDITO / c.total)}</td>
                <td className="num px-3 py-2 text-right">{reais(c.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold">Clientes com CNPJ e regime</h2>
        <p className="text-[12px] text-tinta-3">Quem é do regime regular aproveita o crédito de IBS/CBS; confirmar o regime dos maiores deixa a simulação mais precisa.</p>
        <TabelaContrapartes contexto={contexto} papel="CLIENTE" />
      </section>
    </div>
  )
}

// ─── Crédito em risco ────────────────────────────────────────────────────────

export function CreditoEmRisco({ irFornecedores }: { irFornecedores: () => void }) {
  const trpc = useTRPC()
  const q = useQuery(trpc.contraparte.creditoEmRisco.queryOptions({ limite: 50 }))
  if (q.isPending) return <p className="text-sm text-tinta-3">Carregando…</p>
  if (q.isError) return <Aviso>{mensagemDeErro(q.error)}</Aviso>
  const r = q.data
  if (!r.totalEntradas) return <EstadoVazio titulo="Sem compras">O crédito em risco aparece depois do envio das notas de entrada.</EstadoVazio>
  const emRisco = r.porCredito.LIMITADO + r.porCredito.NENHUM
  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Crédito em risco</h1>
          <p className="mt-1 max-w-2xl text-sm text-tinta-2">
            Compras que não geram crédito integral de IBS/CBS por causa do regime do fornecedor — a pauta de negociação: pedir o híbrido ou trocar de fornecedor. Valores de compra; o crédito em R$, com alíquota, chega no simulador.
          </p>
        </div>
        <Botao onClick={irFornecedores}>Ajustar regimes</Botao>
      </header>
      <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {(['INTEGRAL', 'LIMITADO', 'NENHUM', 'INDEFINIDO'] as const).map((k) => (
          <div key={k} className="rounded-lg border border-linha bg-painel p-4">
            <p className="text-[11px] uppercase tracking-[0.06em] text-tinta-3">{ROTULO_CREDITO[k]}</p>
            <p className="num mt-1 text-lg font-semibold">{reais(r.porCredito[k])}</p>
            <p className="num text-[12px] text-tinta-2">{pct(r.porCredito[k] / r.totalEntradas)}</p>
          </div>
        ))}
      </section>
      <p className="text-[12px] text-tinta-3">
        Total das entradas: <span className="num">{reais(r.totalEntradas)}</span> (as quatro faixas somam exatamente este valor). Em risco: <span className="num">{reais(emRisco)}</span>.
        {r.dependePremissa > 0 && (
          <> <span className="num">{reais(r.dependePremissa)}</span> dependem de premissa não confirmada (Simples puro × híbrido ou regime desconhecido).</>
        )}
      </p>
      <div className="overflow-x-auto rounded-lg border border-linha bg-painel">
        <table className="w-full min-w-[760px] text-left text-[13px]">
          <thead className="border-b border-linha text-[11px] uppercase tracking-[0.06em] text-tinta-3">
            <tr>
              <th className="px-3 py-2.5 font-medium">Fornecedor</th>
              <th className="px-3 py-2.5 font-medium">Regime</th>
              <th className="px-3 py-2.5 text-right font-medium">Compras</th>
              <th className="px-3 py-2.5 text-right font-medium">% das entradas</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-linha">
            {r.ranking.map((f) => (
              <tr key={f.id}>
                <td className="max-w-[300px] px-3 py-2">
                  <span className="block truncate font-medium">{f.nome ?? '—'}</span>
                  <span className="num block text-[11px] text-tinta-3">{docDe(f.tipoPessoa, f.documento)}</span>
                </td>
                <td className="px-3 py-2"><Regime regime={f.regime} confirmacao={f.confirmacao as Confirmacao} fonte={f.fonte as FonteRegime | null} /></td>
                <td className="num px-3 py-2 text-right">{reais(f.valor)}</td>
                <td className="num px-3 py-2 text-right">{pct(f.percentualDasEntradas)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
